import { Button } from "@admin/components/atoms/Button";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  deleteControlPoint,
  FoodControlPoint,
  updateControlPoint,
} from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface EditControlStatusModalProps extends ModalProps {
  controlPoint?: FoodControlPoint;
}

export function EditControlStatusModal({
  controlPoint,
  ...props
}: EditControlStatusModalProps) {
  const queryClient = useQueryClient();
  const isActive = !controlPoint?.isDeleted;
  const { t } = useTranslation();

  const deactivateMutation = useMutation({
    mutationFn: () => deleteControlPoint(controlPoint?.id ?? ""),
    onSuccess: () => {
      toast.success(
        t("common.Item updated successfully", { item: controlPoint?.name }),
      );
      props.onClose?.();
      queryClient.invalidateQueries({
        queryKey: ["foodControlPoints"],
      });
    },
    onError: () => {
      toast.error(t("common.An error occurred"));
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => updateControlPoint({ ...controlPoint, isDeleted: false }),
    onSuccess: () => {
      toast.success(
        t("common.Item updated successfully", { item: controlPoint?.name }),
      );
      props.onClose?.();
      queryClient.invalidateQueries({
        queryKey: ["foodControlPoints"],
      });
    },
    onError: () => {
      toast.error(t("common.An error occurred"));
    },
  });

  return (
    <Modal
      {...props}
      title={
        isActive
          ? t("common.Deactivate", { name: controlPoint?.name })
          : t("common.Reactivate", { name: controlPoint?.name })
      }
      actionButtons={
        <>
          <Button variant="secondary" type="button" onClick={props?.onClose}>
            {t("common.No, discard this action")}
          </Button>
          <Button
            loading={
              reactivateMutation.isPending || deactivateMutation.isPending
            }
            property={isActive ? "danger" : "success"}
            onClick={() => {
              isActive
                ? deactivateMutation.mutate()
                : reactivateMutation.mutate();
            }}
          >
            {isActive
              ? t("common.Yes, deactivate")
              : t("common.Yes, reactivate")}
          </Button>
        </>
      }
    >
      <h2>
        {isActive
          ? t("common.You are about to deactivate", {
              name: controlPoint?.name,
            })
          : t("common.You are about to reactivate", {
              name: controlPoint?.name,
            })}
      </h2>
    </Modal>
  );
}
